import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";
import DevReel from "@/components/DevReel";
import api, { formatApiError } from "@/lib/api";
import { ICON_COMPONENTS } from "@/lib/iconChoices";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";
import CtaButtonBlocks from "./CtaButtonBlocks";
import RichText from "@/components/RichText";
import { useContactModal } from "@/components/ContactModalProvider";

// Valeurs par défaut identiques au contenu d'origine — reprises ici pour
// que la section reste utilisable même si le backend ne renvoie pas
// encore de props (ex: pendant le premier chargement).
const DEFAULTS = {
  eyebrow: "Agences dev web & mobile",
  headline: "L’infrastructure logicielle pour vos produits les plus",
  headline_highlight: "ambitieux.",
  // Sprint 13 — optionnel : segment de titre affiché APRÈS le dégradé, en
  // texte normal (ex: référence "Assurance" : "adaptées" seul en dégradé
  // puis "à votre vie" en blanc). Vide = comportement d'origine inchangé
  // (tout headline_highlight porte le dégradé, comme avant ce champ).
  headline_highlight_plain: "",
  subheadline: "Des équipes techniques ambitieuses nous confient la conception, le développement et la mise à l’échelle de leurs plateformes web et mobiles.",
  cta_primary_label: "Démarrer maintenant",
  cta_primary_url: "#contact",
  cta_secondary_label: "Voir nos réalisations",
  // Sprint 13 — optionnel : cible du bouton secondaire, "/boutique" par
  // défaut (comportement d'origine inchangé). Accepte aussi un lien "tel:"
  // (ex: référence "Assurance", qui affiche son numéro à la place d'un lien
  // catalogue) — dans ce cas l'icône devient un combiné plutôt qu'une flèche.
  cta_secondary_url: "/boutique",
  badge_label: "Devis gratuit",
  image_url: "",
  // Optionnels (Sprint 12) — "false"/vide par défaut, comportement d'origine
  // inchangé (pas de bloc avis, pas de mini-formulaire de rappel).
  show_rating: "false",
  rating_text: "",
  callback_heading: "",
  // Sprint 13 — optionnel : phrase sous le titre du mini-formulaire de
  // rappel (ex: référence "Assurance" : "Un conseiller vous recontacte
  // rapidement."). Vide = rien n'apparaît, comportement d'origine inchangé.
  callback_subtitle: "",
  callback_cta_label: "Rappelez-moi",
};

function FloatingBadge({ block, className }) {
  const Icon = ICON_COMPONENTS[block.props?.icon] || ArrowRight;
  return (
    <div data-block-key={block.id} className={`hidden sm:flex absolute z-20 items-center gap-3 bg-ink/95 backdrop-blur-sm text-white rounded-card px-4 py-3 shadow-card max-w-[200px] ${className}`}>
      <span className="h-9 w-9 rounded-btn bg-gold/20 flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-gold" />
      </span>
      <div className="min-w-0">
        <RichText as="p" className="text-[12.5px] font-semibold leading-tight" html={block.props?.title} />
        {block.props?.subtitle && <RichText as="p" className="text-[11px] text-white/60 leading-tight" html={block.props.subtitle} />}
      </div>
    </div>
  );
}

// Mini-formulaire de rappel superposé à l'image — même endpoint
// /contact-request que ContactSection/ContactQuoteStepper, juste un point
// d'entrée plus court (nom + téléphone) depuis le hero.
function CallbackForm({ heading, subtitle, ctaLabel, ctaStyle }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const { notifySent } = useContactModal();

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-request", { name, email: "", message: `Demande de rappel — Téléphone : ${phone}`, source: "home" });
      setDone(true);
      notifySent("Votre demande de rappel est bien enregistrée. Nous vous rappelons dans les plus brefs délais.", { name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi, réessayez.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-card p-5 shadow-card text-center">
        <p className="text-[14px] font-semibold text-ink">Merci, on vous recontacte vite !</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="hero-callback-form" className="bg-white rounded-card p-5 shadow-card space-y-3">
      <p className="text-[13.5px] font-semibold text-ink">{heading}</p>
      {subtitle && <p className="text-[12.5px] text-graphite -mt-1.5">{subtitle}</p>}
      <input required placeholder="Votre nom" data-testid="hero-callback-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-btn border-control border-stone bg-input-fill px-3.5 py-2.5 text-[13.5px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
      <input required type="tel" placeholder="Votre téléphone" data-testid="hero-callback-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-btn border-control border-stone bg-input-fill px-3.5 py-2.5 text-[13.5px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
      <button
        type="submit"
        disabled={sending}
        data-testid="hero-callback-submit"
        className={`w-full inline-flex items-center justify-center gap-2 bg-ink text-white py-2.5 text-[13.5px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 ${ctaStyle.className}`}
        style={ctaStyle.style}
        aria-label={!ctaStyle.showText ? ctaLabel : undefined}
      >
        {sending ? "Envoi…" : (
          <>
            {ctaStyle.showIcon && ctaStyle.Icon && <ctaStyle.Icon className="h-4 w-4" />}
            {ctaStyle.showText && ctaLabel}
          </>
        )}
      </button>
    </form>
  );
}

export default function HeroSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  // Filtre par TYPE de bloc : le hero accepte désormais aussi des blocs
  // "cta_button" (boutons supplémentaires, voir CtaButtonBlocks.jsx), qui ne
  // sont pas des badges flottants.
  const badges = (blocks || []).filter((b) => b.type === "hero_badge" && b.enabled !== false && b.props?.title).slice(0, 2);
  const isSecondaryTel = t.cta_secondary_url.startsWith("tel:");
  const primaryStyle = resolveButtonStyle(t, "cta_primary");
  const secondaryStyle = resolveButtonStyle(t, "cta_secondary", {
    autoIcon: isSecondaryTel ? "Phone" : null,
    fallbackIcon: isSecondaryTel ? null : "ArrowRight",
  });
  const callbackStyle = resolveButtonStyle(t, "callback_cta");
  return (
    <section className="relative">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 900px 500px at 15% -10%, rgb(var(--c-gold) / 0.20), transparent 60%), radial-gradient(ellipse 800px 500px at 60% 0%, rgb(var(--c-copper) / 0.16), transparent 55%), radial-gradient(ellipse 700px 450px at 92% -5%, rgb(var(--c-blush) / 0.14), transparent 55%)",
        }}
      />
      <div className={`max-w-[1240px] mx-auto px-6 md:px-10 pt-10 md:pt-16 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center ${t.callback_heading ? "pb-16 md:pb-10" : "pb-10"}`}>
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border-control border-stone bg-white/60 px-4 py-1.5 text-[13px] font-medium text-graphite mb-7 animate-fade-up">
            <span className="h-2 w-2 rounded-full" style={{ background: "linear-gradient(135deg, rgb(var(--c-gold)), rgb(var(--c-copper)))" }} />
            <RichText html={t.eyebrow} />
          </div>
          <h1 className="font-display font-semibold text-[42px] sm:text-[54px] lg:text-[60px] leading-[1.05] tracking-[-0.03em] text-ink animate-fade-up" style={{ animationDelay: "80ms" }}>
            <RichText html={t.headline} />{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(120deg, rgb(var(--c-gold)), rgb(var(--c-copper)))" }}
            >
              <RichText html={t.headline_highlight} />
            </span>
            {t.headline_highlight_plain && (
              <>
                {" "}
                <RichText html={t.headline_highlight_plain} />
              </>
            )}
          </h1>
          <RichText
            as="p"
            preserveParagraphs
            className="text-[18px] leading-[1.6] text-graphite max-w-[560px] mt-6 animate-fade-up"
            style={{ animationDelay: "160ms" }}
            html={t.subheadline}
          />
          <div className="flex flex-wrap items-center gap-4 mt-9 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <a
              href={t.cta_primary_url}
              data-testid="hero-cta-contact"
              className={`inline-flex items-center gap-2 bg-ink text-white px-7 py-3.5 text-[15px] font-semibold hover:opacity-90 transition-opacity ${primaryStyle.className}`}
              style={primaryStyle.style}
              aria-label={!primaryStyle.showText ? t.cta_primary_label : undefined}
            >
              {primaryStyle.showIcon && primaryStyle.Icon && <primaryStyle.Icon className="h-4 w-4" />}
              {primaryStyle.showText && t.cta_primary_label}
            </a>
            {isSecondaryTel ? (
              // Bouton plein (pas un simple lien texte) pour un numéro de
              // téléphone — ex: référence "Assurance", affiché à côté du CTA
              // principal comme une seconde action à part entière.
              <a
                href={t.cta_secondary_url}
                data-testid="hero-cta-shop"
                className={`inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-[15px] font-semibold hover:opacity-90 transition-opacity ${secondaryStyle.className}`}
                style={secondaryStyle.style}
                aria-label={!secondaryStyle.showText ? t.cta_secondary_label : undefined}
              >
                {secondaryStyle.showIcon && secondaryStyle.Icon && <secondaryStyle.Icon className="h-4 w-4" />}
                {secondaryStyle.showText && t.cta_secondary_label}
              </a>
            ) : t.cta_secondary_url.startsWith("/") ? (
              <Link
                to={t.cta_secondary_url}
                data-testid="hero-cta-shop"
                className="inline-flex items-center gap-1.5 text-[15px] font-medium text-ink hover:text-gold transition-colors"
                aria-label={!secondaryStyle.showText ? t.cta_secondary_label : undefined}
              >
                {secondaryStyle.showText && t.cta_secondary_label}{" "}
                {secondaryStyle.showIcon && secondaryStyle.Icon && <secondaryStyle.Icon className="h-4 w-4" />}
              </Link>
            ) : (
              <a
                href={t.cta_secondary_url}
                data-testid="hero-cta-shop"
                className="inline-flex items-center gap-1.5 text-[15px] font-medium text-ink hover:text-gold transition-colors"
                aria-label={!secondaryStyle.showText ? t.cta_secondary_label : undefined}
              >
                {secondaryStyle.showText && t.cta_secondary_label}{" "}
                {secondaryStyle.showIcon && secondaryStyle.Icon && <secondaryStyle.Icon className="h-4 w-4" />}
              </a>
            )}
            <CtaButtonBlocks blocks={blocks} tone="light" />
          </div>
          {t.show_rating === "true" && (
            <div className="flex items-center gap-3 mt-8 animate-fade-up" style={{ animationDelay: "280ms" }}>
              <div className="flex -space-x-2">
                {["#12B76A", "#38BDF8", "#8B5CF6", "#F5A623"].map((c, i) => (
                  <span key={i} className="h-7 w-7 rounded-full border-2 border-white" style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-current text-blush" strokeWidth={0} />
                ))}
              </div>
              {t.rating_text && <p className="text-[13px] text-graphite">{t.rating_text}</p>}
            </div>
          )}
        </div>
        {/* Conteneur externe sans overflow-hidden : les badges flottants et le
            mini-formulaire de rappel (optionnels) doivent pouvoir déborder du
            cadre de l'image, contrairement à son contenu (photo/DevReel). */}
        <div className="relative animate-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-media overflow-hidden border-control border-stone bg-ink" style={{ boxShadow: "0 40px 90px rgba(10,14,23,0.18)" }}>
            {t.image_url ? (
              <img src={t.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <DevReel />
            )}
            <a
              href={t.cta_primary_url}
              data-testid="hero-quote-badge"
              className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 bg-gold text-white text-[11px] tracking-wide px-3 py-1.5 rounded-full font-semibold shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
              {t.badge_label}
            </a>
          </div>

          {badges[0] && (
            <FloatingBadge block={badges[0]} className="-top-4 right-6" />
          )}
          {badges[1] && (
            <FloatingBadge block={badges[1]} className="top-[42%] -right-5" />
          )}
          {t.callback_heading && (
            <div className="absolute left-5 right-5 -bottom-10 z-20">
              <CallbackForm heading={t.callback_heading} subtitle={t.callback_subtitle} ctaLabel={t.callback_cta_label} ctaStyle={callbackStyle} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
